<template>
  <div id="page-advanced-search">
    <div class="filterbar">
      <!-- Dynamically added filters insert BEFORE the Add button -->
      <div
        v-for="f in selectedFilters"
        :key="f._uid"
        class="fb-chip rich"
        :title="f.text"
      >
        <span class="fb-chip-title">{{ f.text }}</span>

        <!-- Controls per filter type -->
        <div class="fb-chip-controls" v-if="f.type === 'date-range'">
          <memlist-date-picker
            style="width:140px;"
            size="sm"
            v-model="f.value.from"
            layout="vertical"
            @input="onChipInputChange(f,{ from: $event || null })"
          ></memlist-date-picker>
          <span class="fb-sep">-</span>
          <memlist-date-picker
            style="width:140px;"
            size="sm"
            v-model="f.value.to"
            layout="vertical"
            @input="onChipInputChange(f,{ to: $event || null })"
          ></memlist-date-picker>
        </div>

        <div class="fb-chip-controls" v-else-if="f.type === 'multiselect-companies'">
          <Multiselect
            style="width: 300px;"
            class="multiselect-blue"
            v-model="local_search_options.companies.company_ids"
            ref="companies_multiselect"
            mode="tags"
            :placeholder="$t('ADVANCED_SEARCH.ALL_COMPANIES')"
            :close-on-select="false"
            :filter-results="false"
            :min-chars="0"
            :resolve-on-load="false"
            :delay="0"
            :searchable="true"
            :options="async function(query) {
              return await fetch_company_options(query);
            }"
          />
        </div>

        <div class="fb-chip-controls" v-else-if="f.type === 'number-range'">
          <memlist-text-input
            style="width: 80px;"
            v-model="f.value.from"
            :placeholder="$t('COMMON.FROM')"
            @input="onChipInputChange(f,{ from: castValue($event) })"
          />
          <span class="fb-sep">-</span>
          <memlist-text-input
            style="width: 80px;"
            v-model="f.value.to"
            :placeholder="$t('COMMON.TO')"
            @input="onChipInputChange(f,{ to: castValue($event) })"
          />
          
        </div>

        <div class="fb-chip-controls" v-else-if="f.type === 'select'">

          <memlist-select-input2
            layout="vertical"
            style="width: 200px;"
            v-model="f.value"
            :items="(f.config && f.config.options) || []"
            @input="onChipInputChange(f, $event)"
          />

        </div>

        <div class="fb-chip-controls" v-else>
          <memlist-text-input
            style="width: 180px;"
            v-model="f.value"
            :placeholder="$t('COMMON.ENTER_VALUE')"
            @input="onChipInputChange(f, $event)"
          />

        </div>

        <button
          type="button"
          class="fb-chip-close"
          aria-text="Remove filter"
          @click="removeFilter(f.key)"
        >
          ×
        </button>
      </div>

      <!-- Add Filter button + popover -->
      <div class="fb-add" ref="addWrap">
        <button
          type="button"
          class="fb-add-btn"
          @click="toggleMenu"
          aria-haspopup="listbox"
          :aria-expanded="isMenuOpen.toString()"
        >
          {{ $t('COMMON.ADD_FILTER') }}
        </button>

        <div v-if="isMenuOpen" class="fb-popover" role="listbox">
          <template v-if="remainingFilters.length">
            <button
              v-for="opt in remainingFilters"
              :key="'opt-' + opt.key"
              type="button"
              role="option"
              class="fb-popover-item"
              @click="addFilter(opt)"
            >
              {{ opt.text }}
            </button>
          </template>
          <div v-else class="fb-popover-empty">No more filters</div>
        </div>
      </div>
    </div>

    <m-row class="mt-4" v-if="show_refresh_button">
      <m-col lg="12" style="padding-right: 0px;">
        <div class="d-flex justify-end">
          <memlist-spinner-icon-button
            ref="refresh-button"
            size="sm"
            svg="/assets/svg/sync-solid.svg"
            :text="$t('COMMON.REFRESH')"
            variant="primary"
            @click="refresh_clicked"
          />
        </div>
        
      </m-col>
    </m-row>
    
    
  </div>
</template>
     
<script>
import { mapGetters } from 'vuex';
import Multiselect from '@vueform/multiselect/dist/multiselect.vue2.js'
import axios from 'axios';
import { toasts } from '@/core/mixins/toastr-helper.mixin.js';

export default {
  name: 'FilterBar',
  components: {
    Multiselect,
  },
  mixins: [ toasts ],
  emits: ['search_options_updated','on_search_count_result','on_search_result'],
  props: {
    show_refresh_button: {
      type: Boolean,
      default: true,
    },
    // Optionally start with some selected filters, including values
    // e.g., [{key:'email',text:'E-mail',type:'select',value:'has'}]
    initialSelectedFilters: {
      type: Array,
      default: () => [],
    },
  },

  watch: {
    'local_search_options.companies.company_ids'() {
      this.$emit('search_options_updated', this.local_search_options);

      this.run_search();
    }
  },
  
  computed: {
    ...mapGetters([
      'currentCompanyId',
      'currentPeriodId',
      'currentUserId',
      'companies',
      'periods',
      'profile',
      'settings',
      'isTHS',
      'sid'
    ]),
    email_status_options() {
      return [
        { text: this.$t('COMMON.ALL'), value: null },
        { text: this.$t('MEMBER.EMAIL_STATUSES.UNKNOWN'), value: 'UNKNOWN' },
        { text: this.$t('MEMBER.EMAIL_STATUSES.BOUNCE'), value: 'BOUNCE' },
        { text: this.$t('MEMBER.EMAIL_STATUSES.DELIVERED'), value: 'DELIVERED' },
        { text: this.$t('MEMBER.EMAIL_STATUSES.SPAM_COMPLAIN'), value: 'SPAM_COMPLAIN' },
        { text: this.$t('MEMBER.EMAIL_STATUSES.UNSUBSCRIBE'), value: 'UNSUBSCRIBE' },
      ]
    },
    member_status_options() {
      return [
        { label: this.$t('MEMBER.STATUSES.MEMBER'), value: 'MEMBER' },
        { label: this.$t('MEMBER.STATUSES.PENDING'), value: 'PENDING' },
        { label: this.$t('MEMBER.STATUSES.REJECTED'), value: 'REJECTED' }
      ]
    },
    skv_options() {
      return [
        { label: this.$t('SKV.SKV_STATUSES.IGNORE'), value: 'IGNORE' },
        { label: this.$t('SKV.SKV_STATUSES.MINOR_IGNORE'), value: 'MINOR_IGNORE' },
        { label: this.$t('SKV.SKV_STATUSES.REGISTERED'), value: 'REGISTERED' },
        { label: this.$t('SKV.SKV_STATUSES.DELETED'), value: 'DELETED' },
        { label: this.$t('SKV.SKV_STATUSES.PEN_REGISTER'), value: 'PEN_REGISTER' },
        { label: this.$t('SKV.SKV_STATUSES.PEN_DELETED'), value: 'PEN_DELETED' },
        { label: this.$t('SKV.SKV_STATUSES.ERR_PERSONNR'), value: 'ERR_PERSONNR' },
        { label: this.$t('SKV.SKV_STATUSES.ERR_DUPLICATE'), value: 'ERR_DUPLICATE' },
      ]
    },
    country_code_options() {
      return this.$t('COUNTRIES').map((item) => ({ text: item.value, value: item.key }));
    },
    renewal_options() {
      return [
        { text: this.$t('COMMON.ALL'), value: null },
        { text: this.$t('ADVANCED_SEARCH.NO_ACTIVE_RENEWAL'), value: 'NO_ACTIVE_RENEWAL' },
        { text: this.$t('ADVANCED_SEARCH.HAS_ACTIVE_RENEWAL'), value: 'HAS_ACTIVE_RENEWAL' },
      ]
    },

    company_options() {
      if (!this.companies) {
        return [];
      }

      let comps = this.companies.map((item) => ({ text: item.shortname && item.shortname.length > 0 ? item.shortname : item.name, value: item.id }));

      comps.sort((a, b) => {
        if (a < b) {
          return -1;
        }

        return 1;
      });

      return comps;
    },
    period_options() {
      if (!this.periods) {
        return [];
      }

      const options = [{ text: this.$t('COMMON.ALL'), value: null }];

      const tmp = this.periods.map((item) => ({ text: item.name, value: item.id }));

      for (const t of tmp) {
        options.push(t);
      }

      return options;
    },

    gender_options() {
      return [
        { text: this.$t('ADVANCED_SEARCH.ALL'), value: null },
        { text: this.$t('COMMON.MAN'), value: 'M' },
        { text: this.$t('COMMON.WOMAN'), value: 'F' },
        { text: this.$t('COMMON.OTHER'), value: 'O' },
        { text: this.$t('COMMON.UNKNOWN'), value: 'U' },
      ]
    },

    exists_missing_all_options() {
      return [
        { value: null, text: this.$t('ADVANCED_SEARCH.ALL') },
        { value: 'EXISTS', text: this.$t('ADVANCED_SEARCH.EXISTS'), color: 'green' },
        { value: 'NOT_EXISTS', text: this.$t('ADVANCED_SEARCH.MISSING'), color: 'red' }
      ];
    },

    yes_no_all_options() {
      return [
        { value: null, text: this.$t('ADVANCED_SEARCH.ALL') },
        { value: 1, text: this.$t('ADVANCED_SEARCH.YES'), color: 'green' },
        { value: 0, text: this.$t('ADVANCED_SEARCH.NO'), color: 'red' }
      ]
    },

    yes_no_all_options_reverse_color() {
      return [
        { value: null, text: this.$t('ADVANCED_SEARCH.ALL') },
        { value: 1, text: this.$t('ADVANCED_SEARCH.YES'), color: 'red' },
        { value: 0, text: this.$t('ADVANCED_SEARCH.NO'), color: 'green' }
      ]
    },

    payment_status_options() {
      return [
        { value: null, text: this.$t('ADVANCED_SEARCH.ALL') },
        { value: 1, text: this.$t('ADVANCED_SEARCH.PAID'), color: 'green'  },
        { value: 0, text: this.$t('ADVANCED_SEARCH.NOT_PAID'), color: 'red'  }
      ]
    },
    region_code_options() {
      return this.region_options;
    },
    // Available extra filters to add
    // Now supports richer filter definitions with `type` and `config`
    // types: 'date-range', 'select', 'number-range', 'text'
    availableFilters() {
      return [
        
        { key: 'company_ids', text: this.$t('ADVANCED_SEARCH.COMPANIES'), type: 'multiselect-companies' },
        { key: 'period_id', text: this.$t('PERIOD.PERIOD'), type: 'select', config: { options: this.period_options } },
        
        { key: 'missing_membership_period_id', text: this.$t('MEMBER.MISSING_MEMBERSHIP_PERIOD_ID'), type: 'select', config: { options: this.period_options } },
        { key: 'has_membership_period_id', text: this.$t('MEMBER.HAS_MEMBERSHIP_PERIOD_ID'), type: 'select', config: { options: this.period_options } },
        { key: 'missing_action_for_period_id', text: this.$t('MEMBER.MISSING_ACTION_FOR_PERIOD_ID'), type: 'select', config: { options: this.period_options } },
        { key: 'has_action_for_period_id', text: this.$t('MEMBER.HAS_ACTION_FOR_PERIOD_ID'), type: 'select', config: { options: this.period_options } },
        
        { key: 'email_status', text: this.$t('MEMBER.EMAIL_STATUS'), type: 'select', config: { options: this.email_status_options } },
        
        { key: 'rdt', text: this.$t('MEMBER.RDT'), type: 'date-range' },
        { key: 'email', text: this.$t('MEMBER.EMAIL'), type: 'select', config: { options: this.exists_missing_all_options }},
        { key: 'age', text: this.$t('MEMBER.AGE'), type: 'number-range', config: { min: 0, max: 120 } },
        { key: 'region_code', text: this.$t('MEMBER.REGION'), type: 'select', config: { options: this.region_code_options } },
      ];
    },

    remainingFilters() {
      const selectedKeys = new Set(this.selectedFilters.map(f => f.key));
      return this.availableFilters.filter(f => !selectedKeys.has(f.key));
    },
  },
  methods: {
    async refresh_clicked() {
      try {
        this.isRefreshing = true;
        // Emit current options (if parent wants to persist them) then run
        this.$emit('search_options_updated', this.local_search_options);
        await this.run_search();

        
      } finally {
        this.isRefreshing = false;
        this.$refs['refresh-button'].stop();
      }
    },
    
    async load_regions() {

      try {
        const res = await axios.get('https://zip.memlist.se/api/v1/regions_communes');

        const regions = res.data.regions;
        this.region_options = [];

        for (var k in regions) {
          this.region_options.push({ value: k, text: regions[k].region });
        }

        this.region_options.sort(function(a, b) {
          if (a.text < b.text) {
            return -1;
          }
          if (a.text > b.text) {
            return 1;
          }
          return 0;
        });

      }
      catch (err) {
        this.toastr('danger', this.$t('COMMON.ERROR'), this.$t('PAGES.SEARCH.UNABLE_LIST_REGIONS'));
      }
    },


    async fetch_company_options(query) {
      if (!this.companies?.length) {
        return []
      }

      let res_companies = [...this.companies];

      if (query?.length) {
        res_companies = this.companies.filter(item => item.name.toLowerCase().includes(query?.toLowerCase()));
      }

      return res_companies.map(item => ({ label: item.name, value: item.company_id }));
    },
    async run_search_count() {
      try {
        this.local_search_options.member_variables.age_to = this.set_int_if_not_null(this.local_search_options.member_variables.age_to);
        this.local_search_options.member_variables.age_from = this.set_int_if_not_null(this.local_search_options.member_variables.age_from);

        const res = await axios.post(`/search/pagination/options/count`, { search_options: this.local_search_options });

        if (res.status === 200) {
          this.$emit('on_search_count_result', res.data.count);
          return;
        }

        this.toastr('danger', this.$t('COMMON.ERROR'), this.$t('ADVANCED_SEARCH.UNABLE_TO_SEARCH'))
      }
      catch (err) {
        this.toastr('danger', this.$t('COMMON.ERROR'), this.$t('ADVANCED_SEARCH.UNABLE_TO_SEARCH'))
      }
    },

    async run_search() {
      try {
        this.local_search_options.member_variables.age_to = this.set_int_if_not_null(this.local_search_options.member_variables.age_to);
        this.local_search_options.member_variables.age_from = this.set_int_if_not_null(this.local_search_options.member_variables.age_from);

        const res = await axios.post(`/search/pagination/options`, { search_options: this.local_search_options });

        this.run_search_count();

        if (res.status === 200) {
          this.$emit('on_search_result', res.data, this.local_search_options);
          return;
        }

        this.toastr('danger', this.$t('COMMON.ERROR'), this.$t('ADVANCED_SEARCH.UNABLE_TO_SEARCH'))
      }
      catch (err) {
        this.toastr('danger', this.$t('COMMON.ERROR'), this.$t('ADVANCED_SEARCH.UNABLE_TO_SEARCH'))
      }

    },


    set_int_if_not_null(variable) {
      if (variable === null || variable === undefined) {
        return variable;
      }

      const r = parseInt(variable+'');

      if (isNaN(r)) {
        return null;
      }

      return r;
    },

    toggleMenu() {
      this.isMenuOpen = !this.isMenuOpen;
    },
    closeMenuOnOutside(e) {
      if (!this.isMenuOpen) return;
      const wrap = this.$refs.addWrap;
      if (wrap && !wrap.contains(e.target)) {
        this.isMenuOpen = false;
      }
    },
    addFilter(opt) {
      if (!opt || !opt.key) return;
      if (!this.selectedFilters.find(f => f.key === opt.key)) {
        const withVals = this.withDefaults(opt);
        this.selectedFilters.push({ ...withVals, _uid: this.uuid() });
        this.emitFilters();
      }
      this.isMenuOpen = false;

      if (opt.type === 'multiselect-companies') {
        setTimeout(() => {
          if (this.$refs.companies_multiselect[0]) {
            this.$refs.companies_multiselect[0].refreshOptions()
          }
        }, 500);
      }

      this.run_search();
    },
    removeFilter(key) {
      const idx = this.selectedFilters.findIndex(f => f.key === key);
      if (idx !== -1) {
        this.selectedFilters.splice(idx, 1);
        this.emitFilters();
      }

      if (key === 'missing_membership_period_id') {
        this.local_search_options.member_variables.missing_membership_period_id = null;
      }
      else if (key === 'has_membership_period_id') {
        this.local_search_options.member_variables.has_membership_period_id = null;
      }
      else if (key === 'missing_action_for_period_id') {
        this.local_search_options.member_variables.missing_action_for_period_id = null;
      }
      else if (key === 'has_action_for_period_id') {
        this.local_search_options.member_variables.has_action_for_period_id = null;
      }
      else if (key === 'period_id') {
        this.local_search_options.member_variables.period_id = null;
      }
      else if (key === 'region_code') {
        this.local_search_options.member_variables.region_code = null;
      }
      else if (key === 'email_status') {
        this.local_search_options.member_variables.email_status = null;
      }
      else if (key === 'age') {
        this.local_search_options.member_variables.age_from = null;
        this.local_search_options.member_variables.age_to = null;
      }
      else if (key === 'rdt') {
        this.local_search_options.member_variables.rdt_from = null;
        this.local_search_options.member_variables.rdt_to = null;
      }
      else {
        return;
      }

      this.$emit('search_options_updated', this.local_search_options);

      this.run_search();
    },
    emitFilters() {
      // Emit a lean payload: { key, value } while preserving texts/types
      const payload = this.selectedFilters.map(({ key, text, type, value }) => ({ key, text, type, value }));
      this.$emit('change:filters', payload);
    },
    castValue(val) {
      if (val === '' || val === 'null') return null;
      const n = Number(val);
      return isNaN(n) ? val : n;
    },
    uuid() {
      return 'f-' + Math.random().toString(36).slice(2, 9);
    },
    withDefaults(filterDef) {
      const def = { type: 'text', text: filterDef.key, config: {}, ...filterDef };
      let value;
      switch (def.type) {
        case 'date-range':
          value = { from: null, to: null };
          break;
        case 'number-range':
          value = { from: null, to: null };
          break;
        case 'select':
          // choose first option or 'all' if present
          if (def.config && Array.isArray(def.config.options) && def.config.options.length) {
            const all = def.config.options.find(o => o.value === 'all');
            value = all ? 'all' : def.config.options[0].value;
          } else {
            value = null;
          }
          break;
        default:
          value = '';
      }
      return { ...def, value };
    },
    onChipInputChange(f, patch) {

      console.log(f, patch);

      // patch may be a value (for select/text) or a partial object for ranges
      if (typeof patch === 'object' && patch !== null) {
        f.value = { ...f.value, ...patch };
      } else {
        f.value = patch;
      }


      if (f.key === 'missing_membership_period_id') {
        this.local_search_options.member_variables.missing_membership_period_id = f.value;
        this.run_search();
        this.$emit('search_options_updated', this.local_search_options);
      }
      else if (f.key === 'has_membership_period_id') {
        this.local_search_options.member_variables.has_membership_period_id = f.value;
        this.run_search();
        this.$emit('search_options_updated', this.local_search_options);
      }
      else if (f.key === 'missing_action_for_period_id') {
        this.local_search_options.member_variables.missing_action_for_period_id = f.value;
        this.run_search();
        this.$emit('search_options_updated', this.local_search_options);
      }
      else if (f.key === 'has_action_for_period_id') {
        this.local_search_options.member_variables.has_action_for_period_id = f.value;
        this.run_search();
        this.$emit('search_options_updated', this.local_search_options);
      }
      else if (f.key === 'period_id') {
        console.log('period updated', f.value);
        this.local_search_options.member_variables.period_id = f.value;
        this.run_search();
        this.$emit('search_options_updated', this.local_search_options);
      }
      else if (f.key === 'region_code') {
        this.local_search_options.member_variables.region_code = f.value;
        this.run_search();
        this.$emit('search_options_updated', this.local_search_options);
      }
      else if (f.key === 'email_status') {
        this.local_search_options.member_variables.email_status = f.value;
        this.run_search();
        this.$emit('search_options_updated', this.local_search_options);
      }
      else if (f.key === 'age') {
        if (patch.from !== undefined) {
          this.local_search_options.member_variables.age_from = patch.from;
        }
        else if (patch.to !== undefined) {
          this.local_search_options.member_variables.age_to = patch.to;
        }

        this.run_search();
        this.$emit('search_options_updated', this.local_search_options);
      }
      else if (f.key === 'rdt') {
        if (patch.from !== undefined) {
          this.local_search_options.member_variables.rdt_from = patch.from;
        }
        else if (patch.to !== undefined) {
          this.local_search_options.member_variables.rdt_to = patch.to;
        }

        this.run_search();
        this.$emit('search_options_updated', this.local_search_options);
      }

      this.emitFilters();
    },
  },
  mounted() {
    this.load_regions();
    document.addEventListener('click', this.closeMenuOnOutside);
    setTimeout(()=>{
      this.run_search();
    }, 500);
  },
  beforeDestroy() {
    document.removeEventListener('click', this.closeMenuOnOutside);
  },
  data() {
    return {
      isRefreshing: false,
      region_options: [],
      local_search_options: {

        source: 'MEMBER', // MEMBER, SENDOUT, RENEW
        entity_type: 'MEMBER', // MEMBER, USER, PERSON
        is_sendout: false,

        shop_item_ids: [],

        // list of values
        properties: {
          include: 'ANY', // ANY/ALL
          prop_ids: [], // array of ids
        },

        memberships: {
          include: 'ANY', // ANY/ALL
          shop_item_ids: [] // array of ids
        },

        events: {
          include: 'ANY', // ANY/ALL
          event_ids: [], // array of ids
        },

        educations: {
          include: 'ANY',
          education_ids: [],
        },

        companies: {
          include: 'ANY',
          company_ids: [], // array of company ids
        },

        page: 1,
        limit: 100,
        order_by: {
          public_id: 'desc'
        },

        query: {
          id: null,
          name: null
        },

        audience_id: null,

        

        member_variables: {
          missing_membership_period_id: null,
          has_membership_period_id: null,
          missing_action_for_period_id: null,
          has_action_for_period_id: null,
        

          // simple values
          skv_period_id: null,
          skv_statuses: [],
          statuses: [],
          period_id: null, // int id
          membertype_id: null, // id
          renewal: null,
          secret: null,
          do_autorenew: null,
          email_status: null,

          no_mc_from: null, // yyyy-mm-dd
          no_mc_to: null, // yyyy-mm-dd
          exist_mc_from: null, // yyyy-mm-dd
          exist_mc_to: null, // yyyy-mm-dd
          rdt_from: null, // yyyy-mm-dd
          rdt_to: null, // yyyy-mm-dd
          deactivate_from: null, // yyyy-mm-dd
          deactivate_to: null, // yyyy-mm-dd
          age_from: null, // 0 - 100
          age_to: null, // 0 - 100
          region_code: null, // string

          sex: null, // M, F, U, O
          public_sex: null, // M, F, U, O

          email: null, // EXISTS, NOT_EXISTS
          phone: null, // EXISTS, NOT_EXISTS
          address: null, // EXISTS, NOT_EXISTS
          zipcode: null, // EXISTS, NOT_EXISTS
          country: null, // EXISTS, NOT_EXISTS
          country_code: null, // string
          shop_item: null, // EXISTS, NOT_EXISTS
          personnr: null, // EXISTS, NOT_EXISTS

          deactivated: 0, // 1, 0
          deceased: 0, // 1, 0
          hd: null, // 1, 0
          payment_status: null, // 1, 0
        },

      },
      isMenuOpen: false,
      selectedFilters: this.initialSelectedFilters.length
        ? this.initialSelectedFilters.map(f => ({ ...this.withDefaults(f), _uid: this.uuid() }))
        : [],
    };
  },
};
</script>

<style scoped>
.multiselect-blue {
  --ms-tag-bg: #DBEAFE;
  --ms-tag-color: #2563EB;
}

.filterbar {
  display: flex;
  flex-wrap: wrap; /* allows pushing to a new row when we hit the end */
  gap: 8px; /* space between elements */
  align-items: flex-end;
}

.fb-field {
  display: inline-flex;
  flex-direction: column;
  gap: 4px;
}

.fb-text {
  font-size: 12px;
  color: #555;
}

.fb-select {
  height: 36px;
  min-width: 160px;
  padding: 0 10px;
  border: 1px solid #d0d5dd;
  border-radius: 10px;
  background: #fff;
}

.fb-chip {
  display: inline-flex;
  align-items: center;
  height: 36px;
  padding: 0 8px 0 10px;
  border-radius: 999px;
  background: #eef2ff; /* subtle indigo tint */
  border: 1px solid #c7d2fe;
  gap: 8px;
}

.fb-chip-text {
  font-size: 14px;
}

.fb-chip-close {
  border: none;
  background: transparent;
  cursor: pointer;
  font-size: 18px;
  line-height: 1;
  padding: 0 2px;
  color: #4f46e5;
}

.fb-add {
  position: relative; /* for the popover */
}

.fb-add-btn {
  height: 36px;
  padding: 0 12px;
  border-radius: 10px;
  background: #f4f4f5;
  border: 1px solid #e5e7eb;
  cursor: pointer;
}

.fb-add-btn:hover {
  background: #eaeaec;
}

.fb-popover {
  position: absolute;
  top: calc(100% + 6px);
  left: 0;
  min-width: 220px;
  background: white;
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  box-shadow: 0 6px 20px rgba(0,0,0,0.08);
  padding: 6px;
  z-index: 50;
}

.fb-popover-item {
  display: block;
  width: 100%;
  text-align: left;
  padding: 8px 10px;
  border-radius: 8px;
  border: none;
  background: transparent;
  cursor: pointer;
}

.fb-popover-item:hover, .fb-popover-item:focus {
  background: #f3f4f6;
}

.fb-popover-empty {
  padding: 10px;
  color: #6b7280;
  font-size: 14px;
}
</style>
<style scoped>
.filterbar {
  display: flex;
  flex-wrap: wrap; /* allows pushing to a new row when we hit the end */
  gap: 8px; /* space between elements */
  align-items: flex-end;
}

.fb-field {
  display: inline-flex;
  flex-direction: column;
  gap: 4px;
}

.fb-text {
  font-size: 12px;
  color: #555;
}

.fb-select {
  height: 36px;
  min-width: 160px;
  padding: 0 10px;
  border: 1px solid #d0d5dd;
  border-radius: 10px;
  background: #fff;
}

.fb-chip {
  display: inline-flex;
  align-items: center;
  height: 36px;
  padding: 0 8px 0 10px;
  border-radius: 999px;
  background: #f4f5f5;
  border: 1px solid #a6d9ff;
  gap: 8px;
}

.fb-chip.rich {
  height: 60px;
  padding: 8px 10px;
  border-radius: 4px;
}

.fb-chip-title {
  font-size: 12px;
  color: #005c9f;
  font-weight: 600;
}

.fb-chip-controls {
  display: inline-flex;
  align-items: center;
  gap: 6px;
}

.fb-input {
  height: 32px;
  padding: 0 8px;
  border: 1px solid #d0d5dd;
  border-radius: 8px;
  background: #fff;
  min-width: 140px;
}

.fb-input-narrow { min-width: 90px; }

.fb-sep { color: #6b7280; padding: 0 2px; }

.fb-chip-close {
  border: none;
  background: transparent;
  cursor: pointer;
  font-size: 18px;
  line-height: 1;
  padding: 0 2px;
  color: #4f46e5;
}

.fb-add { position: relative; }

.fb-add-btn {
  height: 36px;
  padding: 0 12px;
  border-radius: 10px;
  background: #f4f4f5;
  border: 1px solid #e5e7eb;
  cursor: pointer;
}
.fb-add-btn:hover { background: #eaeaec; }

.fb-popover {
  position: absolute;
  top: calc(100% + 6px);
  left: 0;
  min-width: 220px;
  background: white;
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  box-shadow: 0 6px 20px rgba(0,0,0,0.08);
  padding: 6px;
  z-index: 50;
  height: 400px;
  overflow-y: scroll;
}

.fb-popover-item {
  display: block;
  width: 100%;
  text-align: left;
  padding: 8px 10px;
  border-radius: 8px;
  border: none;
  background: transparent;
  cursor: pointer;
}
.fb-popover-item:hover, .fb-popover-item:focus { background: #f3f4f6; }

.fb-popover-empty { padding: 10px; color: #6b7280; font-size: 14px; }


</style>
